import { useCallback, useEffect, useMemo, useState } from 'react';
import { API_BASE_URL, API_ENDPOINTS } from '../../constants/api';
import { apiFetch } from '../../utils/apiClient';

const parseApiError = async (response, fallback) => {
  try {
    const payload = await response.json();
    return payload?.detail || payload?.message || fallback;
  } catch (_err) {
    return fallback;
  }
};

export const useApplicationCatalog = ({ enabled = false, activeApplicationId = '' } = {}) => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(Boolean(enabled));
  const [error, setError] = useState('');
  // `enabled` turns on once the session resolves. Report loading from that
  // same render: otherwise consumers see "enabled, no applications, not
  // loading" until the fetch effect runs, which renders "Application not found".
  const [loadingEnabled, setLoadingEnabled] = useState(Boolean(enabled));
  if (loadingEnabled !== Boolean(enabled)) {
    setLoadingEnabled(Boolean(enabled));
    setLoading(Boolean(enabled));
  }

  const loadApplications = useCallback(async () => {
    if (!enabled) {
      setApplications([]);
      setLoading(false);
      setError('');
      return [];
    }
    setLoading(true);
    setError('');
    try {
      const response = await apiFetch(`${API_BASE_URL}${API_ENDPOINTS.APPLICATIONS}`);
      if (!response.ok) {
        throw new Error(await parseApiError(response, 'Failed to load applications.'));
      }
      const payload = await response.json();
      const rows = Array.isArray(payload?.applications) ? payload.applications : [];
      setApplications(rows);
      return rows;
    } catch (err) {
      setApplications([]);
      setError(err?.message || 'Failed to load applications.');
      return [];
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    loadApplications();
  }, [loadApplications]);

  const createApplication = useCallback(async ({ name }) => {
    const normalizedName = String(name || '').trim();
    if (!normalizedName) {
      throw new Error('Application name is required.');
    }
    const response = await apiFetch(`${API_BASE_URL}${API_ENDPOINTS.APPLICATIONS}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: normalizedName, source_type: 'application' }),
    });
    if (!response.ok) {
      throw new Error(await parseApiError(response, 'Failed to create application.'));
    }
    const payload = await response.json();
    const application = payload?.application || null;
    await loadApplications();
    return application;
  }, [loadApplications]);

  const updateApplication = useCallback(async (applicationId, updates) => {
    const normalizedId = String(applicationId || '').trim();
    if (!normalizedId) {
      throw new Error('Application is required.');
    }
    const response = await apiFetch(
      `${API_BASE_URL}${API_ENDPOINTS.APPLICATION_BY_ID(encodeURIComponent(normalizedId))}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates || {}),
      }
    );
    if (!response.ok) {
      throw new Error(await parseApiError(response, 'Failed to update application.'));
    }
    const payload = await response.json();
    await loadApplications();
    return payload?.application || null;
  }, [loadApplications]);

  const activeApplication = useMemo(
    () => applications.find(
      (application) => String(application?.id || '') === String(activeApplicationId || '')
    ) || null,
    [activeApplicationId, applications]
  );

  return {
    applications,
    activeApplication,
    loading,
    error,
    loadApplications,
    createApplication,
    updateApplication,
  };
};
